import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, MenuItem, Stack, InputAdornment,
  Avatar, Tooltip, Alert, Skeleton, Card, CardContent, IconButton,
  Pagination, Select, FormControl,
} from '@mui/material';
import {
  Search, CreditCard, CheckCircle, Cancel, HourglassEmpty,
  ContentCopy, WorkspacePremium, TrendingUp,
} from '@mui/icons-material';
import { premiumAPI } from '../services/api';
import { formatCurrency } from '../utils/currency';
import PageHeader from '../components/PageHeader';

const LIMIT = 20;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtAmount(paise) {
  return formatCurrency((paise || 0) / 100);
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function truncate(str, n = 20) {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  paid: {
    label: 'Success',
    icon: <CheckCircle sx={{ fontSize: 13 }} />,
    bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0',
  },
  failed: {
    label: 'Failed',
    icon: <Cancel sx={{ fontSize: 13 }} />,
    bg: '#fff1f2', text: '#dc2626', border: '#fecdd3',
  },
  created: {
    label: 'Pending',
    icon: <HourglassEmpty sx={{ fontSize: 13 }} />,
    bg: '#fefce8', text: '#ca8a04', border: '#fef08a',
  },
};

// ── Copy button ───────────────────────────────────────────────────────────────
function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton
        size="small"
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        sx={{ p: 0.25, ml: 0.25 }}
      >
        <ContentCopy sx={{ fontSize: 11, color: copied ? '#16a34a' : '#9CA3AF' }} />
      </IconButton>
    </Tooltip>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color, loading }) {
  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', borderRadius: 2 }}>
      <CardContent sx={{ p: '16px !important' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: `${color}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
              {label}
            </Typography>
            {loading
              ? <Skeleton width={60} height={28} />
              : <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, mt: 0.25 }}>{value}</Typography>
            }
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Payments() {
  const [rows, setRows]             = useState([]);
  const [stats, setStats]           = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatus]   = useState('all');
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [total, setTotal]           = useState(0);
  const [limit, setLimit]           = useState(LIMIT);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when filter / search changes
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch]);

  // Load stats once on mount
  useEffect(() => {
    setStatsLoading(true);
    premiumAPI.getTransactionStats()
      .then(r => setStats(r.data.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  // Load paginated transactions
  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch)        params.search  = debouncedSearch;

      const res = await premiumAPI.getTransactions(params);
      const d   = res.data;
      setRows(d.data   || []);
      setTotal(d.total || 0);
      setPages(d.pages || 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, debouncedSearch]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Pagination helpers
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  return (
    <Box>
      {/* ── Header ── */}
      <PageHeader
        title="Payments"
        subtitle="Track premium subscription payments, failures, pending orders, and revenue."
        gradient="linear-gradient(90deg, #16A34A, #4ADE80)"
      />

      {/* ── Stat cards ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <StatCard loading={statsLoading} icon={<TrendingUp />}      label="Total Revenue"
          value={stats ? fmtAmount(stats.revenue) : '—'}
          sub={stats ? `${stats.paid} paid` : ''}
          color="#16a34a" />
        <StatCard loading={statsLoading} icon={<CheckCircle />}     label="Successful"
          value={stats?.paid ?? '—'} sub="Payments captured" color="#16a34a" />
        <StatCard loading={statsLoading} icon={<Cancel />}          label="Failed"
          value={stats?.failed ?? '—'} sub="Payment failures" color="#dc2626" />
        <StatCard loading={statsLoading} icon={<HourglassEmpty />}  label="Pending"
          value={stats?.pending ?? '—'} sub="Orders created" color="#ca8a04" />
      </Box>

      {/* ── Filters ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by email, name, order ID or transaction ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <TextField
          select label="Status" value={statusFilter}
          onChange={e => setStatus(e.target.value)}
          size="small" sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="paid">Success</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="created">Pending</MenuItem>
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Table ── */}
      <TableContainer component={Paper}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
        <Table sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <TableRow sx={{ '& .MuiTableCell-root': { bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
              <TableCell sx={{ minWidth: 180 }}>User</TableCell>
              <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
              <TableCell sx={{ minWidth: 80 }}>Amount</TableCell>
              <TableCell sx={{ minWidth: 200, display: { xs: 'none', md: 'table-cell' } }}>Order ID</TableCell>
              <TableCell sx={{ minWidth: 220, display: { xs: 'none', md: 'table-cell' } }}>Transaction ID</TableCell>
              <TableCell sx={{ minWidth: 120, display: { xs: 'none', sm: 'table-cell' } }}>Premium Until</TableCell>
              <TableCell sx={{ minWidth: 150, display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? [...Array(limit)].map((_, i) => (
                  <TableRow key={i} sx={{ minHeight: 56 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={32} height={32} />
                        <Box><Skeleton width={110} height={13} /><Skeleton width={140} height={11} sx={{ mt: 0.5 }} /></Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Skeleton width={70} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                    <TableCell><Skeleton width={50} height={13} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={140} height={13} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={140} height={13} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={80} height={13} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={100} height={13} /></TableCell>
                  </TableRow>
                ))
              : rows.length === 0
              ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
                        <Box sx={{ width: 80, height: 80, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                          <CreditCard sx={{ fontSize: 40, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
                          {search || statusFilter !== 'all' ? 'No matching transactions' : 'No transactions yet'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: 'auto' }}>
                          {search || statusFilter !== 'all'
                            ? 'Try adjusting your search or filter to find what you\'re looking for.'
                            : 'Premium subscription payments will appear here once users start subscribing.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              : rows.map(tx => {
                  const sc = STATUS_CONFIG[tx.status] || STATUS_CONFIG.created;
                  return (
                    <TableRow key={tx.id}
                      sx={{ '&:hover': { bgcolor: 'action.hover' }, transition: 'background-color 0.15s' }}>

                      {/* User */}
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700 }}>
                            {tx.user?.name?.charAt(0)?.toUpperCase() || '?'}
                          </Avatar>
                          <Box>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2" fontWeight={600}>
                                {tx.user?.name || 'Unknown'}
                              </Typography>
                              {tx.user?.isPremium && (
                                <Tooltip title="Active premium">
                                  <WorkspacePremium sx={{ fontSize: 13, color: '#f0a500' }} />
                                </Tooltip>
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {tx.user?.email || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          px: 1, py: 0.25, borderRadius: 1,
                          bgcolor: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                          fontSize: '0.7rem', fontWeight: 700,
                        }}>
                          {sc.icon}{sc.label}
                        </Box>
                      </TableCell>

                      {/* Amount */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{fmtAmount(tx.amount)}</Typography>
                      </TableCell>

                      {/* Order ID */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack direction="row" alignItems="center">
                          <Tooltip title={tx.razorpayOrderId || '—'}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                              {truncate(tx.razorpayOrderId, 22)}
                            </Typography>
                          </Tooltip>
                          {tx.razorpayOrderId && <CopyButton value={tx.razorpayOrderId} />}
                        </Stack>
                      </TableCell>

                      {/* Transaction ID (Razorpay Payment ID) */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {tx.razorpayPaymentId ? (
                          <Stack direction="row" alignItems="center">
                            <Tooltip title={`Razorpay Payment ID: ${tx.razorpayPaymentId}`}>
                              <Typography
                                component="a"
                                href={`https://dashboard.razorpay.com/app/payments/${tx.razorpayPaymentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                sx={{
                                  fontFamily: 'monospace', fontWeight: 600,
                                  color: '#1a1a2ed4', textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {truncate(tx.razorpayPaymentId, 22)}
                              </Typography>
                            </Tooltip>
                            <CopyButton value={tx.razorpayPaymentId} />
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Premium Until */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">
                          {tx.status === 'paid' ? fmtDate(tx.endDate) : '—'}
                        </Typography>
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDateTime(tx.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination bar — always visible when there's data ── */}
      {total > 0 && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
          sx={{ mt: 2.5, px: 0.5 }}
        >
          {/* Record range */}
          <Typography variant="body2" color="text.secondary">
            Showing {from}–{to} of {total} transactions
          </Typography>

          {/* Page buttons */}
          <Pagination
            count={pages}
            page={page}
            onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            color="primary"
            size="small"
            siblingCount={1}
            boundaryCount={1}
            showFirstButton
            showLastButton
          />

          {/* Per-page selector */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">Per page:</Typography>
            <FormControl size="small">
              <Select
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                sx={{ fontSize: '0.8125rem', minWidth: 70 }}
              >
                {[20, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
